import { CalculationConfig } from '@calculations/types/calculation-config';
import { EditableSnapQuote } from '@snap-types/actions/edit-snap-quote';
import { DocumentWithUser } from '@snap-types/models/document';
import { Decimal } from '@snap-types/types/decimal';

import { ProductOptionType } from '../../types/state/product-option';
import { QuoteInfoTab, QuoteLineType } from '../../types/state/quote-line';
import { QuotePriceType } from '../../types/state/quote-price';
import { emptyLastWin } from './last-win';
import { transformSnapQuotePriceToStoreState } from './transform-quote-price';
import { transformSnapWorkOrderToStoreState } from './transform-work-order';

/**
 * Transforms a quote line without last win or inventory
 */
export function transformPartialQuoteLine(
  quoteLine: Omit<
    EditableSnapQuote['latestVersion']['snapQuoteLines'][number],
    | 'lastWin'
    | 'inventory'
    | 'discountPrices'
    | 'custPriceEffects'
    | 'marketPrices'
    | 'partSpecs'
    | 'craftProductOptions'
  >,
  calculationConfig: CalculationConfig,
): Omit<
  QuoteLineType,
  | 'lastWin'
  | 'inventory'
  | 'discountPrices'
  | 'custPriceEffects'
  | 'marketPrices'
  | 'partSpecs'
  | 'craftProductOptions'
  | 'estQtyOnHand'
> {
  const quotePriceUuids: string[] = [];
  const quotePrices: Record<string, QuotePriceType> = {};
  const documentIds: string[] = [];
  const documents: Record<string, DocumentWithUser> = {};
  const productOptions: Record<string, ProductOptionType> = {};
  const productOptionsUuids: string[] = [];

  let totalLinePrice = new Decimal(0);

  for (const snapQuotePrice of quoteLine.snapQuotePrices) {
    const { uuid, quotePrice, totalPrice } =
      transformSnapQuotePriceToStoreState(snapQuotePrice, calculationConfig);

    quotePriceUuids.push(uuid);
    quotePrices[uuid] = quotePrice;

    totalLinePrice = totalLinePrice.plus(totalPrice);
  }

  for (const document of quoteLine.documents) {
    const { id, fileName, fileType, createdBy, createdAt } = document;
    documentIds.push(id);
    documents[id] = {
      id,
      fileName,
      fileType,
      createdBy: createdBy
        ? {
            displayName: createdBy.displayName,
          }
        : null,
      createdAt,
    };
  }

  for (const productOption of quoteLine.snapProductOptions) {
    const { uuid, code, groupName, optionName, subOptionName } = productOption;
    productOptionsUuids.push(uuid);
    productOptions[uuid] = {
      uuid,
      code: code ?? null,
      groupName,
      optionName,
      subOptionName: subOptionName ?? null,
    };
  }

  return {
    uuid: quoteLine.uuid,
    automatable: quoteLine.automatable,
    priceStrategy: quoteLine.priceStrategy,

    lineNumber: quoteLine.lineNumber,
    customerPartId: quoteLine.customerPartId,
    leadTime: quoteLine.leadTime,
    leadTimeNotes: quoteLine.leadTimeNotes,
    automatedLeadTime: quoteLine.automatedLeadTime ?? null,
    leadTimeSuggestionStatus: quoteLine.leadTimeSuggestionStatus,
    suggestedPricesStatus: quoteLine.suggestedPricesStatus,
    userLeadTime: quoteLine.userLeadTime ?? null,
    specs: quoteLine.specs,
    description: quoteLine.part.description,
    partId: quoteLine.part.partId,
    partOriginalSource: quoteLine.part.originalSource,
    type: quoteLine.part.type,
    material: quoteLine.part.material,
    finishedPartId: quoteLine.finishedPartId,
    configuratorSnapshot: quoteLine.configuratorSnapshot,

    lastWinOrderLineId: quoteLine.lastWinOrderLineId ?? null,

    quotePriceUuids,
    quotePrices,

    documentIds,
    documents,

    status: 'ready',
    isInfoVisible: false,
    infoTabOrderHistoryFilterByCustomer: false,
    infoTabQuoteHistoryFilterByCustomer: false,
    // @TODO(shawk): this should be dependent on the price strategy UI tabs config
    infoTab: QuoteInfoTab.QuoteHistory,

    priceAdjustment: quoteLine.priceAdjustment ?? null,
    priceAdjustmentAmount: quoteLine.priceAdjustmentAmount ?? null,
    priceSegments: quoteLine.priceSegments,
    partInsights: quoteLine.partInsights,

    snapWorkOrder: transformSnapWorkOrderToStoreState(
      quoteLine.snapWorkOrder,
      calculationConfig,
    ),

    productOptionsChangeUnitPrice: quoteLine.productOptionsChangeUnitPrice,
    productOptionsUuids,
    productOptions,

    snapQuoteLineSource: quoteLine.snapQuoteLineSource,
  };
}

/**
 * Transforms a partial quote line into store state, adding necessary defaults
 */
export function transformPartialQuoteLineWithDefaults(
  quoteLine: Omit<
    EditableSnapQuote['latestVersion']['snapQuoteLines'][number],
    | 'lastWin'
    | 'inventory'
    | 'discountPrices'
    | 'custPriceEffects'
    | 'marketPrices'
    | 'partSpecs'
    | 'craftProductOptions'
  >,
  calculationConfig: CalculationConfig,
): QuoteLineType {
  const transformedQuoteLine = transformPartialQuoteLine(
    quoteLine,
    calculationConfig,
  );

  return {
    ...transformedQuoteLine,
    estQtyOnHand: null,
    lastWin: emptyLastWin(),
    inventory: null,
    discountPrices: [],
    custPriceEffects: [],
    marketPrices: [],
    partSpecs: null,
    craftProductOptions: null,
    productOptionsChangeUnitPrice: false,
  };
}

export function transformSnapQuoteLineToStoreState(
  quoteLine: EditableSnapQuote['latestVersion']['snapQuoteLines'][number],
  calculationConfig: CalculationConfig,
): {
  hasLastWin: boolean;
  uuid: string;
  quoteLine: QuoteLineType;
} {
  let lastWin = emptyLastWin();

  if (quoteLine.lastWin) {
    lastWin = {
      hasLastWin: true,
      orderId: quoteLine.lastWin.orderId,
      orderDate: quoteLine.lastWin.orderDate,
      wonAt: quoteLine.lastWin.wonAt,
      leadTime: quoteLine.lastWin.leadTime,
      quantity: quoteLine.lastWin.quantity,
      unitPrice: quoteLine.lastWin.unitPrice.toDecimalPlaces(
        calculationConfig.decimalPlaces,
      ),
      grossMargin: quoteLine.lastWin.grossMargin,
      contributionMargin: quoteLine.lastWin.contributionMargin,
      unitCost: quoteLine.lastWin.unitCost.toDecimalPlaces(
        calculationConfig.decimalPlaces,
      ),
      unitLaborCost: quoteLine.lastWin.unitLaborCost.toDecimalPlaces(
        calculationConfig.decimalPlaces,
      ),
      unitMaterialCost: quoteLine.lastWin.unitMaterialCost.toDecimalPlaces(
        calculationConfig.decimalPlaces,
      ),
      unitServiceCost: quoteLine.lastWin.unitServiceCost.toDecimalPlaces(
        calculationConfig.decimalPlaces,
      ),
      unitVariableCosts: quoteLine.lastWin.unitVariableCosts.toDecimalPlaces(
        calculationConfig.decimalPlaces,
      ),
    };
  }

  const estQtyOnHand = quoteLine.inventory?.qtyOnHand;

  const result = {
    hasLastWin: lastWin.hasLastWin,
    uuid: quoteLine.uuid,
    quoteLine: {
      ...transformPartialQuoteLine(quoteLine, calculationConfig),
      estQtyOnHand,
      lastWin,
      inventory: quoteLine.inventory,
      discountPrices: quoteLine.discountPrices,
      custPriceEffects: quoteLine.custPriceEffects,
      marketPrices: quoteLine.marketPrices,
      partSpecs: quoteLine.partSpecs,
      craftProductOptions: quoteLine.craftProductOptions,
      partInsights: quoteLine.partInsights,
    },
  };

  return result;
}
